import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { hasuraClient } from '../utils/graphqlClient';
import {
  GET_USER_PROFILE_QUERY,
  UPDATE_USER_MUTATION,
  GET_TITLE_BY_ID_QUERY,
} from '../graphql';
import { useNotification } from './useNotification';
import { useAuth } from './useReduxAuth';
import { formatDateForHasura } from '../utils/dateUtils';

const mapRelationshipFromDB = dbValue => {
  const mapping = {
    Anne: 'anne',
    Baba: 'baba',
    Eş: 'es',
    Kardeş: 'kardes',
    Çocuk: 'cocuk',
  };
  return mapping[dbValue] || dbValue.toLowerCase();
};

const mapRelationshipToDB = formValue => {
  const mapping = {
    anne: 'Anne',
    baba: 'Baba',
    es: 'Eş',
    kardes: 'Kardeş',
    cocuk: 'Çocuk',
  };
  return mapping[formValue] || formValue;
};

/**
 * Custom hook for managing user profile operations
 * @param {Object} options - Hook options
 * @param {boolean} options.skipAutoLoad - Skip automatic profile loading on mount
 * @returns {Object} Profile state and operations
 */
export const useProfile = (options = {}) => {
  const { skipAutoLoad = false } = options;
  const { t } = useTranslation();
  const [profileData, setProfileData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const { showNotification } = useNotification();
  const { user } = useAuth();

  /**
   * Fetch user profile data
   */
  const fetchProfile = useCallback(
    async (userId = null) => {
      if (!userId && !user?.id) {
        return;
      }

      const targetUserId = userId || user.id;

      try {
        setIsLoading(true);

        const response = await hasuraClient.query(GET_USER_PROFILE_QUERY, {
          id: targetUserId,
        });

        if (response?.users_by_pk) {
          const userData = response.users_by_pk;

          let titleInfo = null;
          if (userData.title_id) {
            try {
              const titleResponse = await hasuraClient.query(
                GET_TITLE_BY_ID_QUERY,
                {
                  id: userData.title_id,
                }
              );
              titleInfo = titleResponse?.titles_by_pk;
            } catch (titleError) {
              // Title fetch failed, continue without it
            }
          }

          const transformedData = {
            id: userData.id,
            firstName: userData.first_name || '',
            lastName: userData.last_name || '',
            email: userData.email || '',
            personalPhone: userData.phone_number || '',
            address: userData.address || '',
            companyPhone: userData.work_phone_number || '',
            birthDate: userData.user_birthday
              ? new Date(userData.user_birthday)
              : null,
            isLoanRequestManager: userData.is_loan_request_manager || false,
            startDate: userData.work_start_date || null,
            endDate: userData.work_end_date || null,
            annualLeaveBalance: userData.annual_leave_balance ?? 0,
            userAnnualLeaveBalance: userData.user_annual_leave_balance ?? 0,
            totalLeavesUsed: userData.total_leaves_used ?? 0,
            role: userData.role || '',
            title: userData.title_id || '',
            titleName: titleInfo?.title_name || '',
            titleCode: titleInfo?.title_code || '',
            emergencyContactName:
              userData.relative_first_name && userData.relative_last_name
                ? `${userData.relative_first_name} ${userData.relative_last_name}`
                : userData.relative_first_name || '',
            relationshipDegree: mapRelationshipFromDB(
              userData.relative_degree || ''
            ),
            emergencyContactPhone: userData.relative_phone_number || '',
            companyId: userData.company_id,
            team_id: userData.team_id,
            team: userData.team
              ? {
                  id: userData.team.id,
                  name: userData.team.name,
                  group_id: userData.team.group_id,
                  group: userData.team.group
                    ? {
                        id: userData.team.group.id,
                        name: userData.team.group.name,
                      }
                    : null,
                }
              : null,
          };

          setProfileData(transformedData);
          return transformedData;
        } else {
          throw new Error(t('hooks.profile.errors.dataNotFound'));
        }
      } catch (error) {
        showNotification(t('hooks.profile.errors.fetchError'), 'error');
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [user?.id, showNotification, t]
  );

  /**
   * Update user profile
   */
  const updateProfile = useCallback(
    async updateData => {
      if (!profileData?.id) {
        showNotification(t('hooks.profile.errors.noProfileToUpdate'), 'error');
        return { success: false };
      }

      try {
        setIsUpdating(true);

        const dbUpdateData = {};

        if (updateData.firstName !== undefined && updateData.firstName !== '') {
          dbUpdateData.first_name = updateData.firstName;
        }
        if (updateData.lastName !== undefined && updateData.lastName !== '') {
          dbUpdateData.last_name = updateData.lastName;
        }
        if (updateData.email !== undefined && updateData.email !== '') {
          dbUpdateData.email = updateData.email;
        }
        if (updateData.personalPhone !== undefined) {
          dbUpdateData.phone_number = updateData.personalPhone || null;
        }
        if (updateData.address !== undefined) {
          dbUpdateData.address = updateData.address || null;
        }
        if (updateData.companyPhone !== undefined) {
          dbUpdateData.work_phone_number = updateData.companyPhone || null;
        }
        if (updateData.birthDate !== undefined) {
          dbUpdateData.user_birthday = formatDateForHasura(updateData.birthDate);
        }
        if (updateData.emergencyContactName !== undefined) {
          if (updateData.emergencyContactName.trim()) {
            const nameParts = updateData.emergencyContactName.trim().split(' ');
            dbUpdateData.relative_first_name = nameParts[0] || '';
            dbUpdateData.relative_last_name =
              nameParts.slice(1).join(' ') || '';
          } else {
            dbUpdateData.relative_first_name = null;
            dbUpdateData.relative_last_name = null;
          }
        }
        if (updateData.relationshipDegree !== undefined) {
          dbUpdateData.relative_degree =
            mapRelationshipToDB(updateData.relationshipDegree) || null;
        }
        if (updateData.emergencyContactPhone !== undefined) {
          dbUpdateData.relative_phone_number =
            updateData.emergencyContactPhone || null;
        }

        const result = await hasuraClient.mutate(UPDATE_USER_MUTATION, {
          id: profileData.id,
          input: dbUpdateData,
        });

        if (result?.update_users_by_pk) {
          await fetchProfile();

          showNotification(t('hooks.profile.success.updated'), 'success');
          return { success: true, data: result.update_users_by_pk };
        } else {
          throw new Error(t('hooks.profile.errors.updateFailed'));
        }
      } catch (error) {
        const errorMessage =
          error.message || t('hooks.profile.errors.unknownError');
        showNotification(
          t('hooks.profile.errors.updateError', { error: errorMessage }),
          'error'
        );
        return { success: false, error: errorMessage };
      } finally {
        setIsUpdating(false);
      }
    },
    [profileData?.id, showNotification, fetchProfile, t]
  );

  /**
   * Load profile on mount or when user changes
   */
  useEffect(() => {
    if (user?.id && !skipAutoLoad) {
      fetchProfile();
    }
  }, [user?.id, fetchProfile, skipAutoLoad]);

  return {
    profileData,
    isLoading,
    isUpdating,
    fetchProfile,
    updateProfile,
    refetch: fetchProfile,
  };
};
